import { useEffect } from "react";

const MANAGED_ATTRIBUTE = "data-trackme-seo";

const OPEN_GRAPH_META = {
  title: "og:title",
  description: "og:description",
  type: "og:type",
  url: "og:url",
  siteName: "og:site_name",
  locale: "og:locale",
  image: "og:image",
  imageAlt: "og:image:alt",
  imageWidth: "og:image:width",
  imageHeight: "og:image:height",
  imageType: "og:image:type",
};

const TWITTER_META = {
  card: "twitter:card",
  title: "twitter:title",
  description: "twitter:description",
  image: "twitter:image",
  imageAlt: "twitter:image:alt",
  site: "twitter:site",
};

const hasContent = (value) => value !== undefined && value !== null && String(value).trim() !== "";

function syncMeta(attribute, key, content) {
  if (typeof document === "undefined") return;

  const selector = `meta[${attribute}="${key}"]`;
  const elements = Array.from(document.head.querySelectorAll(selector));

  if (!hasContent(content)) {
    elements.forEach((element) => element.remove());
    return;
  }

  const [firstElement, ...duplicates] = elements;
  const element = firstElement || document.createElement("meta");

  element.setAttribute(attribute, key);
  element.setAttribute("content", String(content).trim());
  element.setAttribute(MANAGED_ATTRIBUTE, "true");

  if (!firstElement) {
    document.head.appendChild(element);
  }

  duplicates.forEach((duplicate) => duplicate.remove());
}

function syncCanonical(href) {
  if (typeof document === "undefined") return;

  const elements = Array.from(document.head.querySelectorAll('link[rel="canonical"]'));

  if (!hasContent(href)) {
    elements.forEach((element) => element.remove());
    return;
  }

  const [firstElement, ...duplicates] = elements;
  const element = firstElement || document.createElement("link");

  element.setAttribute("rel", "canonical");
  element.setAttribute("href", href);
  element.setAttribute(MANAGED_ATTRIBUTE, "true");

  if (!firstElement) {
    document.head.appendChild(element);
  }

  duplicates.forEach((duplicate) => duplicate.remove());
}

function syncMetaGroup(attribute, metaMap, values) {
  Object.entries(metaMap).forEach(([field, metaKey]) => {
    syncMeta(attribute, metaKey, values?.[field]);
  });
}

function syncJsonLd(jsonLd) {
  if (typeof document === "undefined") return;

  document.head
    .querySelectorAll(`script[type="application/ld+json"][${MANAGED_ATTRIBUTE}="json-ld"]`)
    .forEach((element) => element.remove());

  const entries = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  entries.filter(Boolean).forEach((entry) => {
    const element = document.createElement("script");
    element.setAttribute("type", "application/ld+json");
    element.setAttribute(MANAGED_ATTRIBUTE, "json-ld");
    element.textContent = JSON.stringify(entry).replace(/</g, "\\u003c");
    document.head.appendChild(element);
  });
}

function resolveAbsoluteUrl(value) {
  if (!hasContent(value) || typeof window === "undefined") return "";

  try {
    return new URL(String(value).trim(), window.location.origin).toString();
  } catch {
    return "";
  }
}

export default function usePageSeo({
  enabled = true,
  title,
  description,
  canonicalPath,
  robots,
  openGraph,
  twitter,
  jsonLd,
} = {}) {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;

    document.title = hasContent(title) ? String(title).trim() : "";

    const canonicalUrl = resolveAbsoluteUrl(canonicalPath);
    const resolvedOpenGraph = openGraph === false
      ? null
      : {
          title,
          description,
          type: "website",
          siteName: "TrackMe",
          locale: "ru_RU",
          ...(openGraph || {}),
        };
    const openGraphValues = resolvedOpenGraph
      ? {
          ...resolvedOpenGraph,
          url: resolveAbsoluteUrl(resolvedOpenGraph.url || canonicalUrl),
          image: resolveAbsoluteUrl(resolvedOpenGraph.image),
        }
      : null;
    const resolvedTwitter = twitter === false
      ? null
      : {
          card: resolvedOpenGraph?.image ? "summary_large_image" : "summary",
          title,
          description,
          image: resolvedOpenGraph?.image,
          imageAlt: resolvedOpenGraph?.imageAlt,
          ...(twitter || {}),
        };
    const twitterValues = resolvedTwitter
      ? {
          ...resolvedTwitter,
          image: resolveAbsoluteUrl(resolvedTwitter.image),
        }
      : null;

    syncMeta("name", "description", description);
    syncMeta("name", "robots", robots);
    syncCanonical(canonicalUrl);
    syncMetaGroup("property", OPEN_GRAPH_META, openGraphValues);
    syncMetaGroup("name", TWITTER_META, twitterValues);
    syncJsonLd(jsonLd);
  }, [enabled, title, description, canonicalPath, robots, openGraph, twitter, jsonLd]);
}
